import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { GenieAccessService } from "@/services/database/GenieAccessService";
import { Agent } from "@/types/agent/agent";
import {
  capturePrivateAccessToken,
  rememberPrivateAccessToken,
} from "@/lib/privateAccessToken";
import { useAuth } from "@/auth/AuthProvider";

export type AgentPageQueryResult =
  | { status: "ok"; agent: Agent }
  | { status: "private"; genieName: string; passwordProtected: boolean }
  | { status: "not_found" }
  | null;

const readPrivateGenieError = (error: unknown) => {
  if (!error || typeof error !== "object" || !("apiError" in error)) {
    return null;
  }
  const apiError = Reflect.get(error, "apiError");
  if (!apiError || typeof apiError !== "object") return null;

  const code = Reflect.get(apiError, "code");
  if (code !== "GENIE_PRIVATE") return null;
  const genieName = Reflect.get(apiError, "genieName");
  return {
    genieName: typeof genieName === "string" ? genieName : "",
    passwordProtected: Reflect.get(apiError, "passwordProtected") === true,
  };
};

export const useAgentPageByUrlName = (urlName?: string | null) => {
  const accessToken = capturePrivateAccessToken();
  const { userProfile } = useAuth();

  return useQuery({
    queryKey: ["agent-page-by-url", urlName, accessToken, userProfile?.id ?? "anonymous"],
    queryFn: async (): Promise<Agent | null> => {
      if (!urlName) return null;
      try {
        const result = await GenieAccessService.getAgentPageByUrlName(
          urlName,
          accessToken,
        );
        if (!result) return null;
        return { ...result.agent, userMemory: result.userMemory };
      } catch {
        return null;
      }
    },
    enabled: !!urlName,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

export const useAgentPageByUrlNameFull = (urlName?: string | null) => {
  const accessToken = capturePrivateAccessToken();
  const { userProfile } = useAuth();

  return useQuery({
    queryKey: ["agent-page-by-url-full", urlName, accessToken, userProfile?.id ?? "anonymous"],
    queryFn: async (): Promise<AgentPageQueryResult> => {
      if (!urlName) return null;
      try {
        const result = await GenieAccessService.getAgentPageByUrlName(
          urlName,
          accessToken,
        );
        if (!result) return { status: "not_found" };
        return {
          status: "ok",
          agent: { ...result.agent, userMemory: result.userMemory },
        };
      } catch (error: unknown) {
        const privateGenie = readPrivateGenieError(error);
        if (privateGenie) {
          return { status: "private", ...privateGenie };
        }
        return { status: "not_found" };
      }
    },
    enabled: !!urlName,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

export const useRequestGenieAccess = () =>
  useMutation({
    mutationFn: ({ urlName, email }: { urlName: string; email: string }) =>
      GenieAccessService.requestAccess(urlName, email),
  });

export const useUnlockGenieWithPassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ urlName, password }: { urlName: string; password: string }) =>
      GenieAccessService.unlockWithPassword(urlName, password),
    onSuccess: ({ token, expires_at }, { urlName }) => {
      rememberPrivateAccessToken(token, expires_at);
      // The page hooks read the token on render. An unchanged private result
      // would not re-render them, so reset the cached result to force a render
      // that picks up the new token and fetches under its key.
      queryClient.resetQueries({ queryKey: ["agent-page-by-url-full", urlName] });
      queryClient.resetQueries({ queryKey: ["agent-page-by-url", urlName] });
    },
  });
};

export const useAcceptGenieInvite = () =>
  useMutation({
    mutationFn: ({
      agentId,
      urlName,
      email,
    }: {
      agentId: string;
      urlName?: string;
      email?: string;
    }) => GenieAccessService.acceptInvite(agentId, { urlName, email }),
  });

export const useAgentUsers = (agentId?: string | null) =>
  useQuery({
    queryKey: ["genie-access-users", agentId],
    queryFn: () => GenieAccessService.getAgentUsers(agentId!),
    enabled: !!agentId,
  });

export const useUpdateAgentUser = (agentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      expiresAt,
    }: {
      userId: string;
      expiresAt: string | null;
    }) => GenieAccessService.updateAgentUser(agentId, userId, expiresAt),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["genie-access-users", agentId],
      }),
  });
};

export const useDeleteAgentUser = (agentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      GenieAccessService.deleteAgentUser(agentId, userId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["genie-access-users", agentId],
      }),
  });
};

export const useAccessRequests = (agentId?: string | null) =>
  useQuery({
    queryKey: ["genie-access-requests", agentId],
    queryFn: () => GenieAccessService.getAccessRequests(agentId!),
    enabled: !!agentId,
  });

export const useResolveAccessRequest = (agentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      solution,
      expiresAt,
    }: {
      requestId: number;
      solution: "approved" | "denied";
      expiresAt?: string | null;
    }) =>
      GenieAccessService.resolveAccessRequest(requestId, solution, expiresAt),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["genie-access-requests", agentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["genie-access-users", agentId],
      });
    },
  });
};

export const useInviteAgentUsers = (agentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      emails,
      expiresAt,
      campaignId,
    }: {
      emails: string[];
      expiresAt?: string | null;
      campaignId: string;
    }) =>
      GenieAccessService.inviteUsers(agentId, emails, campaignId, expiresAt),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["genie-access-users", agentId],
      });
    },
  });
};

export const useAgentPagesByUrlNames = (urlNames: string[]) => {
  const queries = useQueries({
    queries: urlNames.map((urlName) => ({
      queryKey: ["agent-page-by-url", urlName],
      queryFn: async (): Promise<Agent | null> => {
        const result = await GenieAccessService.getAgentPageByUrlName(urlName);
        return result?.agent ?? null;
      },
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    })),
  });

  return queries;
};
